/**
 * Pure membership CDN helpers (no Nuxt imports).
 * Safe to import from media-layer utils during vitest/Nuxt config load.
 */

const AU_PORTAL_HOST_SUFFIXES = ['.staging-au.clientclub.net', '.app-au.clientclub.net']

const MEMBERSHIP_CDN_BY_US_HOST: Record<string, string> = {
  'https://staging.cdnmembership.apisystem.tech': 'https://staging-au.cdnmembership.apisystem.tech',
  'https://cdn.courses.apisystem.tech': 'https://cdn-au.courses.apisystem.tech',
}

/** Browser-only AU portal host detection (no Nuxt / SSR request context). */
export function detectBrowserRegion(): 'us' | 'au' {
  if (typeof window === 'undefined') return 'us'
  const host = window.location.hostname.toLowerCase()
  if (AU_PORTAL_HOST_SUFFIXES.some(suffix => host.includes(suffix))) return 'au'
  if (window.location.search && AU_PORTAL_HOST_SUFFIXES.some(suffix => window.location.search.includes(suffix))) {
    return 'au'
  }
  return 'us'
}

/** Reads `region` / `dataRegion` claims from a JWT payload when present. */
export function readRegionFromToken(token?: string | null): 'us' | 'au' | undefined {
  if (!token || token.split('.').length < 2) return undefined
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    const region = String(payload?.region || payload?.claims?.region || payload?.dataRegion || payload?.data_region || '').toLowerCase()
    if (region === 'au' || region === 'us') return region
  } catch {
    return undefined
  }
  return undefined
}

/**
 * Prefer JWT region when available, otherwise AU portal hostname.
 * Same priority as BaseService `x-dr-region` / init-app DRM pin (without Nuxt SSR host headers).
 */
export function resolveActiveRegion(token?: string | null): 'us' | 'au' {
  return readRegionFromToken(token) ?? detectBrowserRegion()
}

/** Rewrites a membership CDN URL to the AU host when region is AU. US URLs are unchanged. */
export function resolveMembershipCdnUrl(url?: string, region: 'us' | 'au' = resolveActiveRegion()): string {
  if (!url) return ''
  if (region !== 'au') return url
  for (const [usHost, auHost] of Object.entries(MEMBERSHIP_CDN_BY_US_HOST)) {
    if (url.includes(usHost)) return url.replace(usHost, auHost)
  }
  return url
}

/**
 * Index of the US or AU membership CDN host inside a URL. -1 when neither host is present.
 * Always checks both US and mapped AU hosts so recognition does not depend on Nuxt SSR region.
 */
export function membershipCdnIndex(assetUrl: string, usCdnUrl: string, _region?: 'us' | 'au'): number {
  if (!assetUrl || !usCdnUrl) return -1
  const auCdnUrl = resolveMembershipCdnUrl(usCdnUrl, 'au')
  return Math.max(assetUrl.indexOf(usCdnUrl), assetUrl.indexOf(auCdnUrl))
}

/** Object path for any `revex-membership*` GCS URL, including regional AU buckets. */
export function membershipGcsObjectPath(assetUrl: string): string {
  if (!assetUrl) return ''
  const marker = assetUrl.match(/storage\.(?:googleapis|cloud\.google)\.com\/([^?#]+)/i)
  if (!marker) return ''
  const segments = marker[1].split('/').filter(Boolean)
  const bucket = decodeURIComponent(segments[0] || '')
  if (!bucket.startsWith('revex-membership')) return ''
  let rest = segments.slice(1)
  if (rest[0] === 'o') rest = rest.slice(1)
  if (!rest.length) return ''
  return `/${rest.join('/')}`
}
